/**
 * Página de Información Organizativa - Portal de Transparencia
 * Estilo Awwwards Interior - Estructura organizativa de INTERCASA, S.A.
 */

import React, { useRef, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import LocalizedLink from '../../components/common/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Información Organizativa con estilo Awwwards
 * @returns {JSX.Element} Página de información organizativa
 */
const OrganizativeInfoPage = () => {
  const { t } = useTranslation(['transparencyRules', 'transparency']);
  const heroRef = useRef(null);
  const location = useLocation();

  /**
   * Efecto para hacer scroll al inicio de la página al cargar
   */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section - Estilo Portal Transparencia */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <LocalizedLink 
              to="/transparencia"
              className="group inline-flex items-center text-white/60 hover:text-white mb-8 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium tracking-wide">{t('transparency:backToPortal')}</span>
            </LocalizedLink>
            
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] mb-6 drop-shadow-lg">
              <Trans i18nKey="hero.title" ns="transparencyRules" components={{ hl: <span className="text-intercasa-yellow" /> }} />
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              {t('hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto">

            {/* Normativa Aplicable */}
            <div id="normativa" className="scroll-mt-32">
              <AnimatedSection animation="fadeUp">
                <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                  {t('legal.label')}
                </span>
                <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl mb-8">
                  {t('legal.title')}
                </h2>
            
              <div className="space-y-6">
                {/* Operaciones de la empresa */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                    {t('legal.operations.title')}
                  </h3>
                  <p className="text-intercasa-dark/70 leading-relaxed">
                    {t('legal.operations.text')}
                  </p>
                </div>

                {/* Actividades subvencionadas */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                    {t('legal.subsidised.title')}
                  </h3>
                  <ul className="space-y-3 text-intercasa-dark/70">
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.subsidised.decree36" ns="transparencyRules" components={{ lnk: <a href="https://www.gobiernodecanarias.org/boc/2009/068/001.html" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.subsidised.ley11" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/buscar/act.php?id=BOE-A-2007-3825" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.subsidised.rd477" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/diario_boe/txt.php?id=BOE-A-2021-10824" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.subsidised.ley4" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2021-14237" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.subsidised.ley50" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/buscar/doc.php?id=BOE-A-1986-85" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.subsidised.rd303" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/diario_boe/txt.php?id=BOE-A-2015-4667" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.subsidised.rd899" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/buscar/act.php?id=BOE-A-2007-13908" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.subsidised.ley12" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2015-1114" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.subsidised.ley19" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2013-12887" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.subsidised.lopd" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/buscar/act.php?id=BOE-A-2018-16673" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                  </ul>
                </div>

                {/* Normativa mercantil/contable */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                    {t('legal.general.title')}
                  </h3>
                  <ul className="space-y-3 text-intercasa-dark/70">
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.general.lsc" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/buscar/act.php?id=BOE-A-2010-10544" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.general.audit" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/buscar/act.php?id=BOE-A-2015-8147" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span><Trans i18nKey="legal.general.rd1" ns="transparencyRules" components={{ lnk: <a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2021-1350" target="_blank" rel="noopener noreferrer" className="text-intercasa-red hover:underline">enlace</a> }} /></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-intercasa-red mr-2">•</span>
                      <span>{t('legal.general.other')}</span>
                    </li>
                  </ul>
                </div>

                {/* Botones de descarga */}
                <div className="flex flex-wrap gap-4 justify-center mt-8">
                  <a 
                    href="/PortalTansparenciaIntercasa/NormativaIntercasa/NormativaAplicableIntercasa.pdf"
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white font-lato font-medium rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('transparency:download.pdf')}
                  </a>
                  <a 
                    href="/PortalTansparenciaIntercasa/NormativaIntercasa/NormativaAplicableIntercasa.odt"
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white font-lato font-medium rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('transparency:download.odt')}
                  </a>
                </div>
              </div>
              </AnimatedSection>
            </div>

            {/* Estatutos Sociales */}
            <div id="estatutos" className="scroll-mt-32 mt-20">
              <AnimatedSection animation="fadeUp">
                <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                  {t('statutes.label')}
                </span>
                <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl mb-8">
                  {t('statutes.title')}
                </h2>

                <div className="space-y-6">
                  {/* Solicitud de estatutos */}
                  <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                      {t('statutes.heading')}
                    </h3>
                    <p className="text-intercasa-dark/70 leading-relaxed mb-4">
                      {t('statutes.p1')}
                    </p>
                    <a 
                      href="https://sede.registradores.org/sede/sede-corpme-web/home" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-intercasa-red hover:underline font-medium mb-4"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      sede.registradores.org
                    </a>
                    <p className="text-intercasa-dark/70 leading-relaxed mb-4">
                      <Trans i18nKey="statutes.p2" ns="transparencyRules" components={{ bold: <strong /> }} />
                    </p>
                    <p className="text-intercasa-dark/70 leading-relaxed mb-6">
                      <Trans i18nKey="statutes.p3" ns="transparencyRules" components={{ mail: <a href="mailto:notificaciones@grumasa.es" className="text-intercasa-red hover:underline font-medium">notificaciones@grumasa.es</a> }} />
                    </p>
                  </div>
                </div>

                {/* Botones de descarga */}
                <div className="flex flex-wrap gap-4 justify-center mt-8">
                  <a 
                    href="/PortalTansparenciaIntercasa/EstatutosSociales/estatutos.pdf"
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white font-lato font-medium rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('transparency:download.pdf')}
                  </a>
                  <a 
                    href="/PortalTansparenciaIntercasa/EstatutosSociales/estatutos.odt"
                    download
                    className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white font-lato font-medium rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('transparency:download.odt')}
                  </a>
                </div>
              </AnimatedSection>
            </div>

            {/* Información General */}
            <div id="informacion-general" className="scroll-mt-32 mt-20">
              <AnimatedSection animation="fadeUp">
                <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                  {t('history.label')}
                </span>
                <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl mb-8">
                  {t('history.title')}
                </h2>

                <div className="space-y-6">
                  {/* Historia */}
                  <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                    <div className="space-y-4 text-intercasa-dark/70 leading-relaxed">
                      <p>
                        {t('history.p1')}
                      </p>
                      <p>
                        {t('history.p2')}
                      </p>
                      <p>
                        {t('history.p3')}
                      </p>
                      <p>
                        {t('history.p4')}
                      </p>
                      <p>
                        {t('history.p5')}
                      </p>
                      <p>
                        {t('history.p6')}
                      </p>
                      <p>
                        {t('history.p7')}
                      </p>
                    </div>
                  </div>

                  {/* Objeto Social */}
                  <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-4">
                      {t('history.purpose.title')}
                    </h3>
                    <ul className="space-y-3 text-intercasa-dark/70">
                      <li className="flex items-start">
                        <span className="text-intercasa-red mr-2">•</span>
                        <span>{t('history.purpose.item1')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-intercasa-red mr-2">•</span>
                        <span>{t('history.purpose.item2')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-intercasa-red mr-2">•</span>
                        <span>{t('history.purpose.item3')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-intercasa-red mr-2">•</span>
                        <span>{t('history.purpose.item4')}</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-intercasa-red mr-2">•</span>
                        <span>{t('history.purpose.item5')}</span>
                      </li>
                    </ul>
                  </div>

                  {/* Datos Registrales */}
                  <div className="bg-intercasa-dark rounded-2xl p-6 md:p-8 text-white">
                    <h3 className="font-baskerville font-bold text-intercasa-yellow text-xl mb-4">
                      {t('history.registry.title')}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-white mb-3">{t('history.registry.constitution.label')}</h4>
                        <p className="text-white/70 text-sm leading-relaxed">
                          {t('history.registry.constitution.text')}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-3">{t('history.registry.mercantile.label')}</h4>
                        <ul className="text-white/70 text-sm space-y-1">
                          <li>{t('history.registry.mercantile.sheet')}</li>
                          <li>{t('history.registry.mercantile.volume')}</li>
                          <li>{t('history.registry.mercantile.folio')}</li>
                          <li>{t('history.registry.mercantile.cif')}</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-3">{t('history.registry.address.label')}</h4>
                        <p className="text-white/70 text-sm">
                          Carretera Gral. Del Sur, KM.42,5<br />
                          Barranco De Juncalillo, Juan Grande<br />
                          Las Palmas
                        </p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-3">{t('history.registry.contact.label')}</h4>
                        <ul className="text-white/70 text-sm space-y-1">
                          <li>{t('history.registry.contact.phone')} <a href="tel:928732001" className="text-intercasa-yellow hover:underline">928 732 001</a></li>
                          <li>{t('history.registry.contact.fax')}</li>
                          <li>{t('history.registry.contact.email')} <a href="mailto:info@intercasacanarias.es" className="text-intercasa-yellow hover:underline">info@intercasacanarias.es</a></li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Botones de descarga Información General */}
                  <div className="flex flex-wrap gap-4 justify-center mt-8">
                    <a 
                      href="/PortalTansparenciaIntercasa/InformaciónGeneral/informacion.pdf"
                      download
                      className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white font-lato font-medium rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t('transparency:download.pdf')}
                    </a>
                    <a 
                      href="/PortalTansparenciaIntercasa/InformaciónGeneral/informacion.odt"
                      download
                      className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white font-lato font-medium rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t('transparency:download.odt')}
                    </a>
                  </div>

                  {/* Organigrama */}
                  <div className="bg-intercasa-bone/30 rounded-2xl p-8 border border-intercasa-bone">
                    <h3 className="font-baskerville font-bold text-intercasa-dark text-xl mb-6 text-center">
                      {t('orgchart.title')}
                    </h3>
                    <div className="text-center">
                      <img 
                        src="/images/organigrama/organigramaIntercasa.webp" 
                        alt={t('orgchart.alt')}
                        className="max-w-full h-auto mx-auto rounded-xl shadow-lg"
                      />
                      <p className="text-sm text-intercasa-gray mt-6">
                        {t('orgchart.caption')}
                      </p>
                    </div>
                    
                    {/* Botones de descarga */}
                    <div className="flex flex-wrap gap-4 justify-center mt-6">
                      <a 
                        href="/PortalTansparenciaIntercasa/organigramaIntercasa/organigrama.pdf" 
                        download
                        className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('transparency:download.pdf')}
                      </a>
                      <a 
                        href="/PortalTansparenciaIntercasa/organigramaIntercasa/organigrama.odt" 
                        download
                        className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('transparency:download.odt')}
                      </a>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default OrganizativeInfoPage;
